const DESKTOP_BREAKPOINT = 1024;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULT_LIMIT = 6;

class MinerwaHeader extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('.minerwa-header__root');
    this.spacer = this.querySelector('.minerwa-header__spacer');
    this.triggers = [...this.querySelectorAll('[data-menu-trigger]')];
    this.panels = [...this.querySelectorAll('[data-menu-panel]')];
    this.drawer = this.querySelector('#minerwa-mobile-drawer');
    this.drawerToggle = this.querySelector('[data-drawer-toggle]');
    this.accordions = [...this.querySelectorAll('[data-drawer-accordion]')];

    this.collapseAt = Number(this.dataset.collapseAt) || 40;
    this.activeMenu = null;

    this.onScroll = this.onScroll.bind(this);
    this.onRootLeave = this.onRootLeave.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.pageWrapper = this.closest('.page-wrapper') || document.querySelector('.page-wrapper');

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.pageWrapper?.addEventListener('scroll', this.onScroll, { passive: true });
    this.root.addEventListener('mouseleave', this.onRootLeave);
    this.addEventListener('keydown', this.onKeyDown);

    this.bindMenuTriggers();
    this.bindDrawer();

    this.resizeObserver = new ResizeObserver(() => this.syncSpacer());
    this.resizeObserver.observe(this.root);

    this.onScroll();
    this.syncSpacer();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.pageWrapper?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  getScrollTop() {
    return Math.max(window.scrollY || 0, this.pageWrapper?.scrollTop || 0);
  }

  syncSpacer() {
    const height = this.root.offsetHeight;
    this.spacer.style.setProperty('--mnw-spacer-height', `${height}px`);
    document.documentElement.style.setProperty('--minerwa-header-height', `${height}px`);

    if (this.dataset.scrolled !== 'true') {
      document.documentElement.style.setProperty('--minerwa-header-expanded', `${height}px`);
    }
  }

  onScroll() {
    const scrolled = this.getScrollTop() > this.collapseAt;

    if (String(scrolled) !== this.dataset.scrolled) {
      this.dataset.scrolled = String(scrolled);
    }

    this.closeMenu();
    this.closeDrawer();
  }

  bindMenuTriggers() {
    this.triggers.forEach((trigger) => {
      const id = trigger.dataset.menuTrigger;

      trigger.addEventListener('mouseenter', () => this.openMenu(id));

      trigger.addEventListener('click', () => {
        if (this.activeMenu === id) {
          this.closeMenu();
        } else {
          this.openMenu(id);
        }
      });

      trigger.addEventListener('focus', () => {
        if (window.innerWidth >= DESKTOP_BREAKPOINT) this.openMenu(id);
      });
    });

    this.root.addEventListener('focusout', (event) => {
      if (!this.root.contains(event.relatedTarget)) this.closeMenu();
    });
  }

  openMenu(id) {
    if (this.activeMenu === id) return;
    this.activeMenu = id;

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.menuPanel === id;
      panel.hidden = !isActive;
      panel.dataset.open = String(isActive);
    });

    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.menuTrigger === id));
    });
  }

  closeMenu() {
    if (!this.activeMenu) return;
    this.activeMenu = null;

    this.panels.forEach((panel) => {
      panel.hidden = true;
      panel.dataset.open = 'false';
    });

    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  }

  onRootLeave() {
    this.closeMenu();
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;

    if (this.activeMenu) {
      this.closeMenu();
      return;
    }

    if (this.drawer.dataset.open === 'true') this.closeDrawer();
  }

  bindDrawer() {
    this.drawerToggle.addEventListener('click', () => {
      if (this.drawer.dataset.open === 'true') {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    });

    this.accordions.forEach((trigger) => {
      const panel = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        this.accordions.forEach((other) => {
          other.setAttribute('aria-expanded', 'false');
          this.querySelector(`#${other.getAttribute('aria-controls')}`).hidden = true;
        });

        if (!isOpen) {
          trigger.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    });
  }

  openDrawer() {
    this.drawer.hidden = false;
    this.drawer.dataset.open = 'true';
    this.drawerToggle.setAttribute('aria-expanded', 'true');
  }

  closeDrawer() {
    if (this.drawer.dataset.open !== 'true') return;
    this.drawer.hidden = true;
    this.drawer.dataset.open = 'false';
    this.drawerToggle.setAttribute('aria-expanded', 'false');
  }
}

class MinerwaSearch extends HTMLElement {
  connectedCallback() {
    this.context = this.dataset.context;
    this.input = this.querySelector('[data-search-input]');
    this.panel = this.querySelector('[data-search-panel]');
    this.results = this.querySelector('[data-search-results]');
    this.form = this.querySelector('form');
    this.suggestUrl = this.dataset.suggestUrl || '/search/suggest';

    this.debounceTimer = null;
    this.abortController = null;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.input.addEventListener('input', () => this.onInput());

    this.form.addEventListener('submit', (event) => event.preventDefault());

    if (this.context === 'mobile') {
      this.querySelector('[data-search-open]').addEventListener('click', () => this.open());
      this.querySelector('[data-search-close]').addEventListener('click', () => this.close());
    } else {
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.results.innerHTML) this.open();
      });
    }

    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('mousedown', this.onDocumentClick);
    clearTimeout(this.debounceTimer);
    this.abortController?.abort();
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    const term = this.input.value.trim();

    if (!term) {
      this.results.innerHTML = '';
      if (this.context === 'desktop') this.close();
      return;
    }

    this.renderSkeleton();
    this.open();

    this.debounceTimer = setTimeout(() => this.fetchResults(term), SEARCH_DEBOUNCE_MS);
  }

  async fetchResults(term) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product',
      'resources[limit]': SEARCH_RESULT_LIMIT,
      section_id: 'minerwa-predictive-search',
    });

    try {
      const response = await fetch(`${this.suggestUrl}?${params}`, {
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const markup = await response.text();
      const payload = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('[data-search-payload]');

      if (payload) this.results.replaceChildren(...payload.childNodes);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.results.innerHTML = `<p class="minerwa-search__empty">Search is unavailable right now. <a href="${this.form.action}?q=${encodeURIComponent(term)}">See all results</a></p>`;
    }
  }

  renderSkeleton() {
    const card = `
      <div>
        <div class="minerwa-search__skeleton-media"></div>
        <div class="minerwa-search__skeleton-line" style="width: 33%"></div>
        <div class="minerwa-search__skeleton-line" style="width: 80%"></div>
        <div class="minerwa-search__skeleton-line" style="width: 25%"></div>
      </div>
    `;
    this.results.innerHTML = `<div class="minerwa-search__grid">${card.repeat(SEARCH_RESULT_LIMIT)}</div>`;
  }

  open() {
    if (this.panel.dataset.open === 'true') return;

    this.panel.hidden = false;
    requestAnimationFrame(() => {
      this.panel.dataset.open = 'true';
    });

    if (this.context === 'mobile') {
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.input.focus();
    } else {
      document.addEventListener('mousedown', this.onDocumentClick);
    }
  }

  close() {
    if (this.panel.dataset.open !== 'true' && this.panel.hidden) return;

    this.panel.dataset.open = 'false';

    if (this.context === 'mobile') {
      document.body.style.overflow = this.previousOverflow ?? '';
      this.panel.addEventListener(
        'transitionend',
        () => {
          if (this.panel.dataset.open === 'false') this.panel.hidden = true;
        },
        { once: true },
      );
    } else {
      this.panel.hidden = true;
      document.removeEventListener('mousedown', this.onDocumentClick);
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.close();
  }
}

function anchorOffset() {
  const styles = getComputedStyle(document.documentElement);
  const collapsed = parseInt(styles.getPropertyValue('--minerwa-header-collapsed'), 10);
  const current = parseInt(styles.getPropertyValue('--minerwa-header-height'), 10);
  return (Number.isNaN(collapsed) ? current || 0 : collapsed) + 24;
}

function scrollToHash(hash, behavior) {
  if (!hash || hash.length < 2) return false;

  let target = null;
  try {
    target = document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    target = document.getElementById(hash.slice(1));
  }
  if (!target) return false;

  const wrapper = document.querySelector('.page-wrapper');
  const usesWrapper = wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1;
  const offset = anchorOffset();

  if (usesWrapper) {
    wrapper.scrollTo({ top: target.getBoundingClientRect().top + wrapper.scrollTop - offset, behavior });
  } else {
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior });
  }

  return true;
}

function settleHash() {
  if (!window.location.hash) return;

  const attempt = () => scrollToHash(window.location.hash, 'auto');

  requestAnimationFrame(attempt);
  setTimeout(attempt, 120);
  setTimeout(attempt, 600);
  window.addEventListener('load', () => setTimeout(attempt, 60), { once: true });
  window.addEventListener('pageshow', () => {
    requestAnimationFrame(() => requestAnimationFrame(attempt));
  });
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href*="#"]');
  if (!link || link.target === '_blank') return;

  const url = new URL(link.href, window.location.href);
  if (url.pathname !== window.location.pathname || url.origin !== window.location.origin) return;
  if (!url.hash || url.hash.length < 2) return;

  if (scrollToHash(url.hash, 'smooth')) {
    event.preventDefault();
    history.pushState(null, '', url.hash);
  }
});

window.addEventListener('hashchange', () => scrollToHash(window.location.hash, 'smooth'));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', settleHash, { once: true });
} else {
  settleHash();
}

if (!customElements.get('minerwa-header')) {
  customElements.define('minerwa-header', MinerwaHeader);
}
if (!customElements.get('minerwa-search')) {
  customElements.define('minerwa-search', MinerwaSearch);
}
